package com.eladfinish.bible.contest.screens.search.usecases.engine

import com.eladfinish.bible.contest.constants.StringConstants
import com.eladfinish.bible.contest.extensions.runIf
import com.eladfinish.bible.contest.screens.search.engine.ChapterSearchInput
import com.eladfinish.bible.contest.screens.search.engine.ChapterSearchMatch
import com.eladfinish.bible.contest.screens.search.engine.ChapterSearchResult
import com.eladfinish.bible.contest.screens.search.engine.ParsedChapterVerse
import com.eladfinish.bible.contest.screens.search.engine.groupRangesOf
import com.eladfinish.bible.contest.screens.search.model.SearchMode
import com.eladfinish.bible.contest.utils.addGershaim
import com.eladfinish.bible.contest.utils.removeWhitespacesAndParshiot
import com.eladfinish.bible.contest.utils.replaceKtivWithQriNew

class SearchChapterUseCase {

    operator fun invoke(
        input: ChapterSearchInput,
    ): ChapterSearchResult {

        // The chapter assets have CRLF line endings. Android's reader hands them over as "\n",
        // but iOS and web return the text as stored — and the blank line that separates the
        // title from the verses was then not found, so the title's "פרק" was read as the first
        // verse's number.
        val chapterText = input.rawChapterText.replace("\r\n", "\n")

        val chapterTitle = parseChapterTitle(chapterText)

        val matches = chapterText
            .parseChapterVerses()
            .flatMap { verse ->
                searchVerse(
                    input = input,
                    verse = verse,
                    bookName = chapterTitle.bookName,
                    chapterName = chapterTitle.chapterName,
                )
            }

        return ChapterSearchResult(
            matches = matches,
            bookName = chapterTitle.bookName,
            chapterName = chapterTitle.chapterName,
        )
    }

    private data class ChapterTitle(
        val bookName: String,
        val chapterName: String,
    )

    private fun parseChapterTitle(
        chapterContent: String,
    ): ChapterTitle {
        val title = chapterContent
            .lineSequence()
            .firstOrNull { it.isNotBlank() }
            .orEmpty()
            .trim()

        val chapterPrefix = " פרק "
        val chapterPrefixIndex = title.indexOf(chapterPrefix)

        if (chapterPrefixIndex == -1) {
            error("Chapter title is not in expected format: $title")
        }

        return ChapterTitle(
            bookName = title.substring(0, chapterPrefixIndex),
            chapterName = title.substring(chapterPrefixIndex + chapterPrefix.length),
        )
    }

    private fun searchVerse(
        input: ChapterSearchInput,
        verse: ParsedChapterVerse,
        bookName: String,
        chapterName: String,
    ): List<ChapterSearchMatch> {
        val verseContent = verse.rawContent
            .prepareVerseContentForSearch(
                withNikud = input.withNikud,
                withTeamim = input.withTeamim,
                onlyQri = input.onlyQri,
            )

        val prefix = "${StringConstants.COLON_SPLITTER} ${verse.verseNumber} "
        val searchableVerse = prefix + verseContent
        val contentOffset = prefix.length

        val results = mutableListOf<ChapterSearchMatch>()
        var startIndex = 0

        while (true) {
            val matchResult = input.preparedRegex.find(
                input = searchableVerse,
                startIndex = startIndex,
            ) ?: break

            val nextStartIndex = maxOf(
                startIndex + 1,
                matchResult.range.first + 1,
            )

            if (nextStartIndex >= searchableVerse.length) {
                break
            }

            startIndex = nextStartIndex
            if (
                !input.firstColonIsPartOfRegex &&
                matchResult.range.first < contentOffset
            ) {
                continue
            }

            val capturedGroupMatches = resolveCapturedGroupMatches(
                input = input,
                matchResult = matchResult,
                searchableVerse = searchableVerse,
                contentOffset = contentOffset,
            )

            val capturedGroups = capturedGroupMatches.map { it.value }
            val groupRanges = capturedGroupMatches.map { it.range }

            results += ChapterSearchMatch(
                bookIndex = input.bookIndex,
                bookName = bookName,
                chapterName = chapterName,
                verseNumber = verse.verseNumber,
                verseContent = verseContent,
                source = "($bookName ${chapterName.addGershaim()}, ${verse.verseNumber.addGershaim()})",
                capturedGroups = capturedGroups,
                groupRanges = groupRanges,
            )
        }

        return results
    }

    private fun resolveCapturedGroupMatches(
        input: ChapterSearchInput,
        matchResult: MatchResult,
        searchableVerse: String,
        contentOffset: Int,
    ): List<CapturedGroupMatch> {
        val groupRanges = groupRangesOf(
            regex = input.preparedRegex,
            input = searchableVerse,
            match = matchResult,
        )

        if (groupRanges != null) {
            return resolveCapturedGroupsFromRanges(
                groupRanges = groupRanges,
                searchableVerse = searchableVerse,
                contentOffset = contentOffset,
            )
        }

        // Only a browser too old to report group positions gets here. The two functions below
        // guess a group's position from its text, and guess wrong when that text repeats.
        return if (input.searchMode == SearchMode.FIRST_LAST_LETTERS_IN_NAME) {
            resolveFirstLastLettersCapturedGroups(
                matchResult = matchResult,
                searchableVerse = searchableVerse,
                contentOffset = contentOffset,
            )
        } else {
            val groupValues = matchResult.groupValues
                .drop(1)
                .filter { it.isNotEmpty() }

            resolveCapturedGroupsForward(
                groupValues = groupValues,
                matchResult = matchResult,
                searchableVerse = searchableVerse,
                contentOffset = contentOffset,
            )
        }
    }

    /**
     * The groups where the regex engine captured them, as the results list and the copy action
     * need them: in reading order and never overlapping, so a group inside an already
     * highlighted group (the repeated word of `((_! ?){7})`) is dropped.
     */
    private fun resolveCapturedGroupsFromRanges(
        groupRanges: List<IntRange?>,
        searchableVerse: String,
        contentOffset: Int,
    ): List<CapturedGroupMatch> {
        val result = mutableListOf<CapturedGroupMatch>()

        groupRanges
            .filterNotNull()
            .filter { !it.isEmpty() && it.first >= contentOffset }
            .sortedBy { it.first }
            .forEach { range ->
                val previousEnd = result.lastOrNull()?.range?.last ?: -1
                val contentRange = (range.first - contentOffset)..(range.last - contentOffset)

                if (contentRange.first > previousEnd) {
                    result += CapturedGroupMatch(
                        value = searchableVerse.substring(range),
                        range = contentRange,
                    )
                }
            }

        return result
    }

    private fun resolveFirstLastLettersCapturedGroups(
        matchResult: MatchResult,
        searchableVerse: String,
        contentOffset: Int,
    ): List<CapturedGroupMatch> {
        val firstGroup = matchResult.groupValues.getOrNull(1).orEmpty()
        val lastGroup = matchResult.groupValues.getOrNull(2).orEmpty()

        if (firstGroup.isEmpty() || lastGroup.isEmpty()) {
            return emptyList()
        }

        val matchEndExclusive = matchResult.range.last + 1

        val firstStart = searchableVerse.indexOf(
            string = firstGroup,
            startIndex = contentOffset,
        )

        val lastStart = searchableVerse.lastIndexOf(
            string = lastGroup,
            startIndex = matchEndExclusive - lastGroup.length,
        )

        if (firstStart == -1 || lastStart == -1) {
            return emptyList()
        }

        return listOf(
            CapturedGroupMatch(
                value = firstGroup,
                range = toContentRange(
                    startInSearchableVerse = firstStart,
                    value = firstGroup,
                    contentOffset = contentOffset,
                ),
            ),
            CapturedGroupMatch(
                value = lastGroup,
                range = toContentRange(
                    startInSearchableVerse = lastStart,
                    value = lastGroup,
                    contentOffset = contentOffset,
                ),
            ),
        ).filter { match ->
            match.range.first >= 0 &&
                    match.range.last >= match.range.first
        }
    }

    private fun resolveCapturedGroupsForward(
        groupValues: List<String>,
        matchResult: MatchResult,
        searchableVerse: String,
        contentOffset: Int,
    ): List<CapturedGroupMatch> {
        val result = mutableListOf<CapturedGroupMatch>()
        var searchStartIndex = matchResult.range.first

        groupValues.forEach { groupValue ->
            val start = searchableVerse.indexOf(
                string = groupValue,
                startIndex = searchStartIndex,
            )

            if (start >= 0 && start <= matchResult.range.last) {
                result += CapturedGroupMatch(
                    value = groupValue,
                    range = toContentRange(
                        startInSearchableVerse = start,
                        value = groupValue,
                        contentOffset = contentOffset,
                    ),
                )

                searchStartIndex = start + groupValue.length
            }
        }

        return result
    }

    private fun toContentRange(
        startInSearchableVerse: Int,
        value: String,
        contentOffset: Int,
    ): IntRange {
        val startInContent = startInSearchableVerse - contentOffset
        val endInclusiveInContent = startInContent + value.length - 1

        return startInContent..endInclusiveInContent
    }

    private data class CapturedGroupMatch(
        val value: String,
        val range: IntRange,
    )

    private fun String.parseChapterVerses(): List<ParsedChapterVerse> {
        val chapterBody = substringAfter("\n\n")
        val verses = mutableListOf<ParsedChapterVerse>()

        var index = 0

        while (index < chapterBody.length) {
            val verseStart = findNextVerseStart(
                text = chapterBody,
                startIndex = index,
            ) ?: break

            val verseContentStart = verseStart.contentStartIndex
            val verseEndIndex = chapterBody.indexOf(
                char = '׃',
                startIndex = verseContentStart,
            )

            if (verseEndIndex == -1) {
                break
            }

            verses += ParsedChapterVerse(
                verseNumber = verseStart.verseNumber,
                rawContent = chapterBody.substring(
                    startIndex = verseContentStart,
                    endIndex = verseEndIndex + 1,
                ),
            )

            index = verseEndIndex + 1
        }

        return verses
    }

    private data class VerseStart(
        val verseNumber: String,
        val contentStartIndex: Int,
    )

    private fun findNextVerseStart(
        text: String,
        startIndex: Int,
    ): VerseStart? {
        var index = startIndex

        while (index < text.length) {
            val verseNumber = readVerseNumberAt(
                text = text,
                index = index,
            )

            if (verseNumber != null) {
                val afterVerseNumber = index + verseNumber.length

                if (
                    afterVerseNumber < text.length &&
                    text[afterVerseNumber].isWhitespace()
                ) {
                    return VerseStart(
                        verseNumber = verseNumber,
                        contentStartIndex = afterVerseNumber + 1,
                    )
                }
            }

            index++
        }

        return null
    }

    private fun readVerseNumberAt(
        text: String,
        index: Int,
    ): String? {
        if (index > 0 && !text[index - 1].isWhitespace()) {
            return null
        }

        if (!text[index].isHebrewLetter()) {
            return null
        }

        var currentIndex = index

        while (
            currentIndex < text.length &&
            text[currentIndex].isHebrewLetter() &&
            currentIndex - index < MAX_VERSE_NUMBER_LENGTH
        ) {
            currentIndex++
        }

        return text.substring(
            startIndex = index,
            endIndex = currentIndex,
        )
    }

    private fun Char.isHebrewLetter(): Boolean {
        return this in 'א'..'ת'
    }

    private fun String.prepareVerseContentForSearch(
        withNikud: Boolean,
        withTeamim: Boolean,
        onlyQri: Boolean,
    ): String {
        // Ketiv is reduced to the qere *before* nikud is stripped: the qere rule tells a ketiv
        // from an ordinary word by its having no nikud, so on already-stripped text it took the
        // word before every ketiv for part of it and deleted that word as well.
        val normalized = replace('\u00A0', ' ')
            .replace(Regex("\\s+"), " ")
            .trim()
            .runIf(condition = onlyQri) {
                replaceKtivWithQriNew()
            }

        return when {
            withNikud && withTeamim -> normalized
            withNikud && !withTeamim -> normalized.removeHebrewTeamim()
            !withNikud && withTeamim -> normalized.removeHebrewNikud()
            else -> normalized.removeHebrewNikudAndTeamim()
        }.removeWhitespacesAndParshiot()
    }

    private fun String.removeHebrewNikudAndTeamim(): String {
        return replace(
            regex = Regex("[\\u0591-\\u05BD\\u05BF-\\u05C2\\u05C4-\\u05C7]"),
            replacement = "",
        )
    }

    private fun String.removeHebrewTeamim(): String {
        return replace(
            regex = Regex("[\\u0591-\\u05AF]"),
            replacement = "",
        )
    }

    private fun String.removeHebrewNikud(): String {
        return replace(
            regex = Regex("[\\u05B0-\\u05BD\\u05BF-\\u05C2\\u05C4-\\u05C7]"),
            replacement = "",
        )
    }

    private companion object {
        const val MAX_VERSE_NUMBER_LENGTH = 3
    }
}
